import React, { useState, FC, useEffect } from "react";
import { Icon, TooltipComponent } from "../../components/Component";
import { Tooltip as AntTooltip } from "antd";
import { useNavigate, useLocation } from "react-router";
import ReturnsAside from "./ReturnsAside";
import { formatCurrency } from "../../utils/Utils";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { Button } from "reactstrap";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const ViewAllReturns: FC = () => {
  const [sm, setSm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const { data: currentUser } = useSelector(
    (state: RootState) => state.currentUser
  );

  const buildNavigationState = () => {
    const previousPath = `${location.pathname}${location.search || ""}`;
    const statePayload: any = {
      previousPath,
    };
    if (location.state !== undefined) {
      statePayload.previousState = location.state;
    }
    return statePayload;
  };

  const navigateToInvoice = (invoiceKey?: string | number) => {
    if (!invoiceKey && invoiceKey !== 0) return;
    const safeKey = encodeURIComponent(String(invoiceKey));
    navigate(`/view-invoice/${safeKey}`, {
      state: buildNavigationState(),
    });
  };

  const navigateToOrder = (orderKey?: string | number) => {
    if (!orderKey && orderKey !== 0) return;
    const safeKey = encodeURIComponent(String(orderKey));
    navigate(`/view-purchase-order/${safeKey}`, {
      state: buildNavigationState(),
    });
  };

  const columns: any = [
    {
      title: "Return #",
      dataIndex: "returnId",
      key: "returnId",
      width: 250,
      fixed: "left",
      render: (text: any) => (
        <div className="align-left">
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Return Date",
      dataIndex: "returnDate",
      key: "returnDate",
      render: (date: any) => (
        <span>{date ? dayjs(date).format("DD-MMM-YYYY") : "N/A"}</span>
      ),
      width: 150,
    },
    {
      title: "Invoice #",
      dataIndex: "invoiceId",
      key: "invoiceId",
      width: 250,
      render: (text: any, record: any) => {
        const displayText = text ?? record?.invoiceNumber ?? "-";
        const invoiceKey =
          record?.invoiceNumber ?? record?.invoiceId ?? text ?? null;
        if (!invoiceKey) {
          return (
            <div className="align-left">
              <span>{displayText}</span>
            </div>
          );
        }
        return (
          <div className="align-left">
            <AntTooltip title="View Invoice Details">
              <span
                className="inlineTableCellLink"
                role="button"
                onClick={() => navigateToInvoice(invoiceKey)}
              >
                {displayText}
              </span>
            </AntTooltip>
          </div>
        );
      },
    },
    {
      title: "Order #",
      dataIndex: "orderId",
      key: "orderId",
      width: 250,
      render: (text: any, record: any) => {
        const displayText = text ?? record?.orderNumber ?? "-";
        const orderKey = record?.orderNumber ?? record?.orderId ?? text ?? null;
        if (!orderKey) {
          return (
            <div className="align-left">
              <span>{displayText}</span>
            </div>
          );
        }
        return (
          <div className="align-left">
            <AntTooltip title="View Purchase Order Details">
              <span
                className="inlineTableCellLink"
                role="button"
                onClick={() => navigateToOrder(orderKey)}
              >
                {displayText}
              </span>
            </AntTooltip>
          </div>
        );
      },
    },
    {
      title: "Supplier",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 250,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`supplierName-tooltip-${index}`}>
              {text?.length > 20 ? `${text?.substring(0, 25)}... ` : text}
            </span>
            {text?.length > 20 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`supplierName-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site",
      dataIndex: "siteName",
      key: "siteName",
      width: 200,
    },
    {
      title: "Return Type",
      dataIndex: "returnType",
      key: "returnType",
      width: 200,
    },
    {
      title: "Basic Amount",
      dataIndex: "basicAmount",
      key: "basicAmount",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, record.currencyCode)}</span>
        </div>
      ),
    },
    {
      title: "GST Amount",
      dataIndex: "gstAmount",
      key: "gstAmount",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, record.currencyCode)}</span>
        </div>
      ),
    },
    {
      title: "Gross Amount",
      dataIndex: "grossAmount",
      key: "grossAmount",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, record.currencyCode)}</span>
        </div>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdUserName",
      key: "createdUserName",
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      width: 180,
      fixed: "right",
      render: (approvalStatus: any, record: any) => {
        const displayStatus = (approvalStatus || record.status || "").trim();
        const statusColors: Record<string, string> = {
          Pending: "#FFA500",
          "Pending Approval": "#FFA500",
          Approved: "#28A745",
          Rejected: "#DC3545",
          "Auto Approved": "#28A745",
        };
        const statusLabels: Record<string, string> = {
          Pending: "Pending Approval",
          "Pending Approval": "Pending Approval",
          Approved: "Approved",
          Rejected: "Rejected",
          "Auto Approved": "Auto Approved",
        };

        const color = statusColors[displayStatus] || "#6C757D";
        const label = statusLabels[displayStatus] || displayStatus || "N/A";

        return (
          <div className="justify-start align-center">
            <h6
              className={`tb-status ms-1`}
              style={{
                color,
                fontSize: "14px",
              }}
            >
              {label}
            </h6>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (text: any, record: any) => (
        <>
          <Button
            size="sm"
            color=""
            onClick={() => {
              navigate(`/view-return-details/${record.returnNumber}`, {
                state: buildNavigationState(),
              });
            }}
          >
            <Icon name="focus" />
          </Button>
        </>
      ),
    },
  ];

  // Note: returnNumber is used for navigation
  // returnId is the display ID

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const options = currentUser.siteAccessibility.map((item: any) => ({
          label: item.siteName,
          value: item.siteID,
        }));
        setSiteOptions(options);
      }
    }
  }, [currentUser]);

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="view-returns"
      title="Purchase Returns - View"
      columns={columns}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <ReturnsAside updateSm={setSm} sm={sm} />
        </div>
      }
      selectFilters={[
        {
          title: "Site",
          placeholder: "Select Site",
          options: siteOptions,
          selectionMode: "multiple",
        },
      ]}
      extra={{
        tableScrollX: 1100,
        dateRange: true,
        dataFilterTootip: "Filtering based on Purchase Return date",
      }}
    />
  );
};

export default ViewAllReturns;
